import type { AgencyModalState } from './agency-modal.interface';
import type { AppState } from '../../../store';
import type { ConnectedProps } from 'react-redux';
import type { UpdateDivisionRequestDto } from 'shared/dtos/divisions.dto';

import { bindActionCreators } from '@reduxjs/toolkit';
import { AgencyForm } from 'components/forms/agency-form/agency-form';
import { PermissionsContext } from 'contexts';
import React, { PureComponent } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { connect } from 'react-redux';
import {
  removeAgencyFromStore,
  setCurrentAgency,
} from 'store/agencies/agencies.slice';
import {
  createAgencyThunk,
  getAgenciesThunk,
  getAgenciesWithDivisionsThunk,
  removeAgencyThunk,
  updateAgencyThunk,
} from 'store/agencies/agencies.thunks';
import { clearSelectedDivisions } from 'store/divisions/divisions.slice';
import {
  getDivisionsThunk,
  updateDivisionThunk,
} from 'store/divisions/divisions.thunks';
import { setFormDirty } from 'store/formIsDirty/formIsDirty.slice';
import { closeModal, openModal } from 'store/modals/modals.slice';
import { clearSelectedPeople } from 'store/people/people.slice';

import { modalService } from '../../../services/modals.service';
import {
  ASSIGN_DIVISION_MODAL,
  ASSIGN_PEOPLE_MODAL,
  MODAL_AGENCY,
} from '../../../shared/constants/modals';
import { ConfirmationModal } from '../../template/modals/confirmation-modal';
import { Modal } from '../../template/modals/modal';
import { NotFoundMessage } from '../../template/not-found-message/not-found-message';
import { Spinner } from '../../template/spinner/spinner';

const mapStateToProps = (state: AppState) => {
  const modal = modalService.getModalDataFromState(state, MODAL_AGENCY);
  const { isDirty } = state.formIsDirty;
  return {
    agencies: state.agencies,
    people: state.people,
    divisions: state.divisions,
    isGetPending: state.agencies.isGetAgencyThunkPending,
    isUpdatePending: state.agencies.isUpdateAgencyThunkPending,
    isUpdateOrCreatePending: state.divisions.isCreateUpdateDivisionThunkPending,
    isDirty,
    ...modal,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      closeModal,
      openModal,
      updateDivisionThunk,
      clearSelectedDivisions,
      clearSelectedPeople,
      removeAgencyFromStore,
      setCurrentAgency,
      createAgencyThunk,
      updateAgencyThunk,
      removeAgencyThunk,
      getAgenciesThunk,
      getAgenciesWithDivisionsThunk,
      getDivisionsThunk,
      setFormDirty,
    },
    dispatch,
  );

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
class AgencyModal extends PureComponent<PropsFromRedux, AgencyModalState> {
  static contextType = PermissionsContext;
  context!: React.ContextType<typeof PermissionsContext>;

  state = {
    isUpdateAllDivisionsPending: false,
  };

  componentDidUpdate(prevProps: Readonly<PropsFromRedux>) {
    if (prevProps.isGetPending && !this.props.isGetPending) {
      this.props.clearSelectedDivisions!();
      this.props.clearSelectedPeople!();
      this.props.setFormDirty(false);
    }
  }

  get isPending() {
    return (
      !!this.props.isGetPending ||
      !!this.props.isUpdatePending ||
      this.state.isUpdateAllDivisionsPending
    );
  }

  setUpdateAllDivisionsPending = (isPending: boolean) => {
    this.setState({ isUpdateAllDivisionsPending: isPending });
  };

  public handleClose = (): void => {
    if (this.isPending) {
      return;
    }

    this.props.removeAgencyFromStore!();
    this.props.clearSelectedDivisions!();
    this.props.clearSelectedPeople!();
    this.props.closeModal({ name: MODAL_AGENCY, data: null });
  };

  public openAssignPeopleModal = (): void => {
    this.props.openModal({ name: ASSIGN_PEOPLE_MODAL, data: 'agency' });
  };

  public saveToCurrentAgency = (values): void => {
    this.props.setCurrentAgency!(values);
  };

  public openAssignDivisionModal = (): void => {
    this.props.openModal({ name: ASSIGN_DIVISION_MODAL });
  };

  public showConfirmationModal = (id): void => {
    confirmAlert({
      customUI: ({ onClose }): JSX.Element => {
        return (
          <ConfirmationModal
            onClose={onClose}
            onOk={() => this.handleDelete(id)}
          />
        );
      },
    });
  };

  public updateDivisions = async (agencyId, divisions): Promise<void> => {
    if (!divisions || this.state.isUpdateAllDivisionsPending) {
      return;
    }

    this.setState({ isUpdateAllDivisionsPending: true });

    const updatesList = divisions
      .filter((division) => division.toUnclaim || division.needToAdd)
      .map((division) => {
        const formData: UpdateDivisionRequestDto = {
          id: division.id,
          title: division.title,
          environments: division.environments,
          missions: division.missions,
          PeopleId: division.PeopleId,
          AgencyId: division.toUnclaim ? null : agencyId,
          Activities: division.Activities,
          ShortTermGoals: division.ShortTermGoals,
        };
        return this.props.updateDivisionThunk(formData);
      });

    await Promise.all(updatesList).finally(() =>
      this.setUpdateAllDivisionsPending(false),
    );
  };

  public handleSubmit = async (
    agencyData,
    combinedDivisions,
  ): Promise<void> => {
    const { people } = this.props;

    if (people!.selected && people!.selected.forAgency) {
      agencyData.PeopleId = people!.selected.forAgency.peopleId;
    }

    const formData = {
      title: agencyData.title,
      description: agencyData.description,
      PeopleId: agencyData.PeopleId,
    };

    await this.updateDivisions(agencyData.id, combinedDivisions);

    if (agencyData.id) {
      // when we UPDATE the Agency behavior
      this.props.updateAgencyThunk({ ...formData, id: agencyData.id });
      return;
    }

    // when we CREATE the Agency behavior
    this.props.createAgencyThunk(formData);
    this.props.closeModal({ name: MODAL_AGENCY, data: null });
  };

  public handleDelete = (id): void => {
    this.props.removeAgencyThunk(id);
    this.props.removeAgencyFromStore!();
    this.props.clearSelectedDivisions!();
    this.props.clearSelectedPeople!();
    this.props.closeModal({ name: MODAL_AGENCY });
  };

  public render(): JSX.Element {
    const { modal, people, agencies, divisions, isDirty } = this.props;

    return people && agencies && divisions ? (
      <React.Fragment>
        {modal.isOpen && (
          <Modal close={this.handleClose} formIsDirty={isDirty}>
            <>
              <AgencyForm
                peopleState={people}
                agenciesState={agencies}
                divisionsState={divisions}
                onSubmit={this.handleSubmit}
                onDelete={this.showConfirmationModal}
                saveToCurrentAgency={this.saveToCurrentAgency}
                openAssignPeopleModal={this.openAssignPeopleModal}
                openAssignDivisionModal={this.openAssignDivisionModal}
              />
              {this.isPending && <Spinner isFullSize />}
            </>
          </Modal>
        )}
      </React.Fragment>
    ) : (
      <NotFoundMessage message={'Here is a problem with react store. '} />
    );
  }
}

export default connector(AgencyModal);
